/**
 * Sprint M5.42 (30.07.2026) — Block A1.
 *
 * /dashboard/begegnungs-dynamik — Pro-only Route.
 *
 * Free-User sehen 404 (via Backend-Response), redirected zu /pricing.
 * Pro-User: Eingabefeld für Gesprächssituation, Analyse via
 * POST /api/begegnungs-dynamik/analyze, Historie via GET /begegnungs-dynamik/history.
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { DoorOpen, DoorClosed, Loader2, RotateCw, Waves, AlertCircle } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import CreditCounter from "@/components/CreditCounter";
import { isPremium } from "@/lib/licensing";
import { api } from "@/lib/api";

const DOOR_META = {
  offen: { icon: DoorOpen, label: "Tür offen", color: "text-emerald-700 bg-emerald-100" },
  halb_offen: { icon: DoorOpen, label: "Tür halb offen", color: "text-amber-700 bg-amber-100" },
  geschlossen: { icon: DoorClosed, label: "Tür geschlossen", color: "text-red-700 bg-red-100" },
};

const WAVE_META = {
  synchron: { label: "synchron", tone: "text-emerald-700" },
  ähnlich: { label: "ähnlich", tone: "text-amber-700" },
  unterschiedlich: { label: "unterschiedlich", tone: "text-red-700" },
  unbekannt: { label: "unbekannt", tone: "text-slate-500" },
};

function ResultCard({ result }) {
  const doorMeta = DOOR_META[result.door_status] || DOOR_META.offen;
  const waveMeta = WAVE_META[result.wellenlaenge] || WAVE_META.unbekannt;
  const DoorIcon = doorMeta.icon;
  return (
    <section
      data-testid="begegnung-result"
      data-door-status={result.door_status}
      className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
    >
      <div className="flex flex-wrap items-start gap-4">
        <div
          data-testid="begegnung-door-status"
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold ${doorMeta.color}`}
        >
          <DoorIcon className="w-5 h-5" />
          {doorMeta.label}
        </div>
        <div
          data-testid="begegnung-wellenlaenge"
          className={`flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 ${waveMeta.tone}`}
        >
          <Waves className="w-4 h-4" />
          <span className="text-sm font-semibold">Wellenlänge: {waveMeta.label}</span>
        </div>
      </div>

      {result.patterns?.length > 0 && (
        <div className="mt-5" data-testid="begegnung-patterns">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Muster erkannt
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
            {result.patterns.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-[#4563BB] font-bold">•</span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.recommendations?.length > 0 && (
        <div className="mt-5" data-testid="begegnung-recommendations">
          <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
            Empfehlungen
          </p>
          <ol className="mt-2 space-y-2 text-sm text-slate-700">
            {result.recommendations.map((r, i) => (
              <li
                key={i}
                data-testid={`begegnung-recommendation-${i}`}
                className="flex gap-2"
              >
                <span className="w-5 h-5 flex-shrink-0 rounded-full bg-blue-100 text-[#4563BB] font-bold text-xs flex items-center justify-center">
                  {i + 1}
                </span>
                {r}
              </li>
            ))}
          </ol>
        </div>
      )}

      {typeof result.credits_remaining === "number" && (
        <p className="mt-5 text-xs text-slate-500 italic" data-testid="begegnung-credits-remaining">
          Du hast noch {result.credits_remaining} KI-Credits übrig.
        </p>
      )}
    </section>
  );
}

function HistorySection({ items }) {
  if (!items || items.length === 0) {
    return (
      <section
        data-testid="begegnung-history-empty"
        className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
      >
        Noch keine Analysen. Deine letzten 10 Analysen erscheinen hier.
      </section>
    );
  }
  return (
    <section data-testid="begegnung-history" className="mt-6 space-y-2">
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
        Letzte {items.length} Analysen
      </p>
      {items.map((item) => {
        const doorMeta = DOOR_META[item.door_status] || DOOR_META.offen;
        return (
          <article
            key={item.id}
            data-testid={`begegnung-history-item-${item.id}`}
            className="rounded-xl border border-slate-200 bg-white p-4"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="text-sm text-slate-700 line-clamp-2 flex-1">{item.situation_text}</p>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${doorMeta.color}`}
              >
                {doorMeta.label}
              </span>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              {new Date(item.created_at).toLocaleString("de-DE")}
            </p>
          </article>
        );
      })}
    </section>
  );
}

export default function DashboardBegegnungsDynamik() {
  return (
    <DashboardLayout>
      {({ data }) => {
        const user = data?.user;
        if (!isPremium(user)) {
          return <Navigate to="/pricing" replace />;
        }
        return <PageContent user={user} />;
      }}
    </DashboardLayout>
  );
}

function PageContent({ user }) {
  const [situationText, setSituationText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  // Sprint M5.42 (30.07.2026) — Fix Iteration 56: CreditCounter refreshen
  // sobald eine Analyse durchgelaufen ist.
  const [creditRefreshKey, setCreditRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/begegnungs-dynamik/history").then(
      ({ data }) => {
        setHistory(data.items || []);
        setHistoryLoading(false);
      },
      () => setHistoryLoading(false),
    );
  }, []);

  const canSubmit = situationText.trim().length >= 20 && !analyzing;

  const submit = async () => {
    if (!canSubmit) return;
    setAnalyzing(true);
    setError(null);
    try {
      const { data } = await api.post("/begegnungs-dynamik/analyze", {
        situation_text: situationText,
      });
      setResult(data);
      // Historie neu laden
      const historyRes = await api.get("/begegnungs-dynamik/history");
      setHistory(historyRes.data.items || []);
      // Sprint M5.42 (30.07.2026) — Credit-Counter im Header refreshen.
      setCreditRefreshKey((k) => k + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "insufficient_credits") {
        setError(
          `Keine Credits übrig (Balance: ${detail.balance ?? 0}). Reset am 1. des nächsten Monats.`,
        );
      } else {
        setError("Analyse fehlgeschlagen. Bitte versuche es erneut.");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 py-10"
      data-testid="dashboard-begegnungs-dynamik-page"
    >
      <header className="mb-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
              Begegnungs-Dynamik
            </p>
            <h1
              className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900"
              data-testid="begegnung-headline"
            >
              Türen öffnen — Wellenlängen finden
            </h1>
          </div>
          <CreditCounter refreshKey={creditRefreshKey} />
        </div>
        <p
          className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl"
          data-testid="begegnung-bigpicture"
        >
          Beschreibe eine Gesprächssituation. Wir analysieren, ob die Tür bei dir und deinem
          Gegenüber offen ist — und was dir hilft, wieder auf Wellenlänge zu kommen. Vollversion
          mit erweiterter KI folgt in Sprint M5.44.
        </p>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <label className="block">
          <span className="text-sm font-bold text-slate-900">
            Deine Gesprächssituation
          </span>
          <textarea
            value={situationText}
            onChange={(e) => setSituationText(e.target.value.slice(0, 2000))}
            data-testid="begegnung-situation-input"
            placeholder="Beschreibe die Situation in ein paar Sätzen — mind. 20 Zeichen."
            className="mt-2 w-full min-h-[140px] rounded-xl border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
            maxLength={2000}
          />
          <p className="mt-1 text-xs text-slate-400" data-testid="begegnung-charcount">
            {situationText.length} / 2000 Zeichen
          </p>
        </label>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="begegnung-submit-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#4563BB] hover:opacity-90 text-white font-bold px-5 py-3 text-sm disabled:opacity-40"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Analysiere…
              </>
            ) : (
              <>
                <RotateCw className="w-4 h-4" /> Analyse starten (−1 Credit)
              </>
            )}
          </button>
        </div>
        {error && (
          <div
            data-testid="begegnung-error"
            className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}
      </section>

      {result && <ResultCard result={result} />}

      {!historyLoading && <HistorySection items={history} />}
    </div>
  );
}
